import { ButtonGradientPrimary, Colors, Icon } from '@amelio/ui-library';

import { ActionMenu, type ActionMenuItem } from './ActionMenu';
import { CompetencyCard } from './CompetencyCard';
import { ChevronDownIcon, ChevronUpIcon, PlusIcon } from './icons';

import type { Competency, CompetencyGroup } from '@/types/competency/Competency';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { CompetencyStatus } from '@/types/competency/Competency';
import { getTextByLanguage } from '@/utils/localization';

type CompetencyGroupSectionProps = {
    group: CompetencyGroup;
    isOpen: boolean;
    openCompetencyIds: Set<string>;
    onToggleGroup: () => void;
    onToggleCompetency: (competencyId: string) => void;
    onAddCompetency: (groupId: string) => void;
    onAssignToEmployees: (competency: Competency) => void;
    groupActions: ActionMenuItem[];
    competencyActions: (competency: Competency, group: CompetencyGroup) => ActionMenuItem[];
};

export const CompetencyGroupSection = ({
    group,
    isOpen,
    openCompetencyIds,
    onToggleGroup,
    onToggleCompetency,
    onAddCompetency,
    onAssignToEmployees,
    groupActions,
    competencyActions,
}: CompetencyGroupSectionProps) => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();

    const isGroupInactive = group.status !== CompetencyStatus.ACTIVE;

    return (
        <div>
            <div className={'group/row mb-6 flex w-full min-w-0 items-center gap-2 sm:gap-3'}>
                <button
                    type={'button'}
                    onClick={onToggleGroup}
                    className={
                        'flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left sm:gap-3' +
                        (isGroupInactive ? ' opacity-60' : '')
                    }
                    data-testid={`competency-group-${group.id}`}
                >
                    <div className={'min-w-0 flex-1'}>
                        <div className={'flex min-w-0 items-center gap-1 sm:gap-2'}>
                            <h2
                                className={
                                    'truncate text-[22px] font-medium ' +
                                    (isGroupInactive ? 'text-gray-500 line-through' : 'text-navy')
                                }
                            >
                                {getTextByLanguage(group.name, language)}
                            </h2>

                            {isGroupInactive && (
                                <span
                                    className={
                                        'flex-shrink-0 rounded-full bg-[#5E5E5E]/10 px-2 py-0.5 text-[10px] font-semibold tracking-wide text-[#5E5E5E] uppercase'
                                    }
                                >
                                    {t('competencies.disabled')}
                                </span>
                            )}
                        </div>

                        <p className={'text-md mt-0.5 truncate text-gray-500'}>
                            {getTextByLanguage(group.description, language)}
                        </p>

                        <span
                            className={
                                'bg-purple/10 text-purple mt-1 inline-block rounded-full px-3 py-0.5 text-xs font-semibold'
                            }
                        >
                            {t('competencies.competencyCount', {
                                count: group.competencies.length,
                            })}
                        </span>
                    </div>

                    <span className={'text-navy flex-shrink-0'}>
                        {isOpen ? (
                            <ChevronUpIcon className={'h-6 w-6'} />
                        ) : (
                            <ChevronDownIcon className={'h-6 w-6'} />
                        )}
                    </span>
                </button>

                <ActionMenu
                    items={groupActions}
                    triggerClassName={
                        'md:opacity-0 md:transition-opacity md:duration-300 md:ease-out md:group-hover/row:opacity-100 md:focus:opacity-100'
                    }
                    dataTestId={`competency-group-actions-${group.id}`}
                />
            </div>

            {isOpen && (
                <div className={'cmp-stack-6'}>
                    {group.competencies.length === 0 && (
                        <div
                            className={
                                'flex flex-col items-center justify-center rounded-2xl border border-dashed border-[#c9d2db] bg-white/40 px-6 py-12 text-center'
                            }
                        >
                            <div
                                className={
                                    'bg-primary/10 mb-4 flex h-16 w-16 items-center justify-center rounded-full'
                                }
                            >
                                <Icon
                                    name={'settings-category'}
                                    size={'xl'}
                                    color={Colors.PRIMARY}
                                />
                            </div>

                            <h4 className={'text-navy text-body font-semibold'}>
                                {t('competencies.emptyGroup')}
                            </h4>

                            <p className={'mt-1.5 max-w-md text-sm text-[#5E5E5E]'}>
                                {t('competencies.emptyGroupHint')}
                            </p>

                            <ButtonGradientPrimary
                                size={'md'}
                                onClick={() => onAddCompetency(group.id)}
                                leftIcon={<PlusIcon className={'h-5 w-5'} />}
                                className={'mt-5'}
                                dataTestId={`competency-group-empty-add-${group.id}`}
                            >
                                {t('competencies.emptyGroupCta')}
                            </ButtonGradientPrimary>
                        </div>
                    )}

                    {group.competencies.map((competency) => (
                        <CompetencyCard
                            key={competency.id}
                            competency={competency}
                            isOpen={openCompetencyIds.has(competency.id)}
                            onToggle={() => onToggleCompetency(competency.id)}
                            onAssignToEmployees={() => onAssignToEmployees(competency)}
                            actions={competencyActions(competency, group)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
